import { stageName } from "./scratch-helpers.js";

const EVENTS_WITH_TARGET_SUFFIX = new Set([
    "create",
    "move",
    "delete",
    "change",
    "svg_updated",
    "bitmap_updated",
    "costume_added",
    "costume_reordered",
    "costume_renamed",
    "costume_duplicated",
    "costume_deleted",
    "sound_added",
    "sound_reordered",
    "sound_renamed",
    "sound_duplicated",
    "sound_updated",
    "sound_deleted",
]);

const displayTargetName = (name) => {
    if (!name) return null;
    return name === stageName ? "el fondo" : name;
};

export default class SessionParser {
    static IMPORTANT_EVENTS = [
        "join",
        "create",
        "move",
        "delete",
        "change",
        "sprite_added",
        "sprite_deleted",
        "sprite_renamed",
        "svg_updated",
        "bitmap_updated",
        "costume_added",
        "costume_reordered",
        "costume_renamed",
        "costume_duplicated",
        "costume_deleted",
        "backdrop_added",
        "sound_added",
        "sound_reordered",
        "sound_renamed",
        "sound_duplicated",
        "sound_updated",
        "sound_deleted",
        "green_flag_pressed",
        "global_flag_pressed",
        "mode_changed",
        "disconnected",
        "sessionEnd",
    ];

    static BASELINE_EVENT = "session_baseline";

    static UI_EVENTS = [
        "block_selected",
        "block_drag_end",
        "join",
        "sessionEnd",
    ];

    constructor(rawText) {
        this.rawText = rawText;
        this.allEvents = [];
        this.timeline = [];
        this.userSessions = {};
        this.parse();
    }

    parse() {
        const lines = this.rawText
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean);

        this.allEvents = lines
            .map((line, idx) => {
                try {
                    return JSON.parse(line);
                } catch (err) {
                    console.warn(
                        `[Parser] Malformed line ${idx + 1}:`,
                        line.slice(0, 80),
                    );
                    return null;
                }
            })
            .filter(Boolean);

        this.allEvents.forEach((evt) => {
            if (evt.eventType === "join") {
                if (!this.userSessions[evt.username])
                    this.userSessions[evt.username] = [];
                this.userSessions[evt.username].push({
                    start: evt.time,
                    end: null,
                });
            } else if (evt.eventType === "sessionEnd" && evt.data?.duration) {
                const sessions = this.userSessions[evt.username];
                if (sessions && sessions.length) {
                    const last = sessions[sessions.length - 1];
                    if (!last.end) last.end = evt.time;
                }
            }
        });

        this.timeline = this.allEvents
            .filter((evt) =>
                SessionParser.IMPORTANT_EVENTS.includes(evt.eventType),
            )
            .sort(
                (a, b) => this._toTimestamp(a.time) - this._toTimestamp(b.time),
            );

        this.timeline = this.timeline.map((evt, idx) =>
            this._normalise(evt, idx),
        );
    }

    _toTimestamp(str) {
        if (!str) return 0;

        const [d, t] = str.split(" - ");
        const [day, month, year] = d.split("/");
        const fullYear = parseInt(year, 10) < 50 ? `20${year}` : `19${year}`;
        return new Date(`${fullYear}-${month}-${day}T${t}`).getTime();
    }

    _normalise(evt, idx) {
        const base = {
            step: idx + 1,
            rawTime: evt.time,
            timestamp: evt.time,
            author: evt.username || "Desconocido",
            eventType: evt.eventType,
            data: evt.data || {},
        };

        const labels = {
            join: "Se unió",
            create: "Creó un bloque",
            move: "Movió un bloque",
            delete: "Eliminó un bloque",
            change: "Cambió un valor",
            sprite_added: "Añadió un objeto",
            sprite_deleted: "Borró un objeto",
            sprite_renamed: "Renombró un objeto",
            svg_updated: "Editó un disfraz",
            bitmap_updated: "Editó un disfraz",
            costume_added: "Añadió un disfraz",
            costume_reordered: "Reordenó un disfraz",
            costume_renamed: "Renombró un disfraz",
            costume_duplicated: "Duplicó un disfraz",
            costume_deleted: "Borró un disfraz",
            backdrop_added: "Añadió un fondo",
            sound_added: "Añadió un sonido",
            sound_reordered: "Reordenó un sonido",
            sound_renamed: "Renombró un sonido",
            sound_duplicated: "Duplicó un sonido",
            sound_updated: "Editó un sonido",
            sound_deleted: "Borró un sonido",
            green_flag_pressed: "Presionó la bandera verde",
            global_flag_pressed: "Presionó la bandera verde para todos",
            mode_changed: "Cambió de modo",
            disconnected: "Se desconectó",
            sessionEnd: "Sesión finalizada",
        };

        base.label = labels[evt.eventType] || evt.eventType;

        if (EVENTS_WITH_TARGET_SUFFIX.has(evt.eventType)) {
            const targetName = displayTargetName(base.data.target);
            if (targetName) base.label += ` en ${targetName}`;
        }

        if (evt.eventType === "delete" && base.data.blId) {
            base.data.blockId = base.data.blId;
        }

        return base;
    }

    getTimeline() {
        return this.timeline;
    }

    getBaselineProject() {
        const evt = this.allEvents.find(
            (e) => e.eventType === SessionParser.BASELINE_EVENT,
        );
        return evt?.data?.project || null;
    }

    get totalSteps() {
        return this.timeline.length;
    }
}
